import { MultiSelectInputOption } from '@/components/form/baseInputs/inputTypes';
import {
  getOwningEntityFromSuggestionStatements,
  SuggestionForKGV2Grouping,
} from '@/modules/aiSuggestions/kgV2Resolution.utils';
import { SubtrustCreatedFromType } from '@/types/schema';

interface CreatedFromSuggestionDetails {
  displayName?: string | null;
  type?: SubtrustCreatedFromType | null;
}

export interface CreatedFromSuggestionForMatching
  extends SuggestionForKGV2Grouping {
  subtrustCreatedFrom?: CreatedFromSuggestionDetails | null;
}

/**
 * Resolve the trust option for a SUBTRUST_CREATED_FROM suggestion.
 *
 * Some created-from suggestions include multiple `distributes_to` statements. In the
 * broken ticket state, the first subject node resolves to another subtrust while a
 * later subject node resolves to the real parent trust entity, so this path only
 * considers subject-side resolved entities.
 *
 * Falls back to a brittle display-name match when statement traversal can't resolve
 * the parent — see {@link findOptionByCreatedFromDisplayName}.
 */
export function findSuggestedCreatedFromOption(
  createdFromSuggestion: CreatedFromSuggestionForMatching | undefined,
  trustEntityOptions: MultiSelectInputOption[]
): MultiSelectInputOption | undefined {
  if (!createdFromSuggestion) {
    return undefined;
  }

  const resolvedEntityId = getOwningEntityFromSuggestionStatements(
    createdFromSuggestion,
    {
      referenceType: 'subject',
      resolvedDataKinds: ['entity'],
    }
  );

  const optionByResolvedId = resolvedEntityId
    ? trustEntityOptions.find((option) => option.value === resolvedEntityId)
    : undefined;

  if (optionByResolvedId) {
    return optionByResolvedId;
  }

  // Statement traversal can't resolve the parent when this subtrust is reached via
  // an intermediary trust (parent -> intermediary -> this subtrust): the parent
  // never appears as a subject node on this suggestion's statements, so there is no
  // node to resolve an ID from. Fall back to matching the inferred parent display
  // name against the available trust options.
  return findOptionByCreatedFromDisplayName(
    createdFromSuggestion,
    trustEntityOptions
  );
}

/**
 * Normalize a display name for comparison: trim, lowercase, and collapse internal
 * whitespace runs to a single space.
 */
function normalizeForMatch(value: string): string {
  return value.trim().toLowerCase().replace(/\s+/g, ' ');
}

/**
 * BRITTLE STOPGAP — see [T2-10532] to harden/replace.
 *
 * The backend's `InferredSubtrustCreatedFromDetails` only carries a `displayName` and
 * `type`, never a resolved entity ID. When the parent trust isn't present as a
 * resolvable node in the suggestion's statements (e.g. distribution flows through an
 * intermediary trust), the only signal we have left is the inferred parent display
 * name, which we string-match against the trust options.
 *
 * This is brittle for two reasons:
 *   1. `displayName` is a document-inferred free-text name (e.g. "John Smith
 *      Revocable Trust") with no contract that it equals an option's in-app
 *      `subtype.displayName`.
 *   2. Households frequently contain trusts with near-identical names.
 *
 * To bound the wrong-trust risk we (a) only attempt this for TRUST-typed parents,
 * since the options are Entities (a SUBTRUST parent is a TestamentaryEntity and can't
 * live in this option set), and (b) only return a match when exactly one option
 * matches the normalized name. Anything ambiguous (0 or >1 matches) resolves to
 * `undefined` so the suggestion stays un-acceptable rather than binding the wrong
 * trust.
 *
 * The real fix is a resolved `entity`/`testamentaryEntity` edge on
 * `InferredSubtrustCreatedFromDetails`, populated by the v2 save layer like the other
 * suggestion kinds already are.
 */
function findOptionByCreatedFromDisplayName(
  createdFromSuggestion: CreatedFromSuggestionForMatching,
  trustEntityOptions: MultiSelectInputOption[]
): MultiSelectInputOption | undefined {
  const { displayName, type } = createdFromSuggestion.subtrustCreatedFrom ?? {};

  if (type !== SubtrustCreatedFromType.Trust || !displayName) {
    return undefined;
  }

  const normalizedTarget = normalizeForMatch(displayName);
  const matches = trustEntityOptions.filter(
    (option) => normalizeForMatch(option.display) === normalizedTarget
  );

  // Only accept an unambiguous, unique match.
  return matches.length === 1 ? matches[0] : undefined;
}
